import {
  CollateralBasketType,
  CollectionType,
  NegotiationInstruction,
  NegotiationStatus,
  Order,
  OrderAttributes,
  OrderAttributesCreateSource,
  OrderDetail,
  OrderUserEnteredAttributes,
  RepoType,
  SettlementVenue,
  SpreadsheetSettlementVenue,
  TradeConditionFlag,
  TradingEntityClearingType,
  UserPreference
} from 'api'
import moment from 'moment'
import { Nullable } from 'utils'
import { OPEN_ORDER_TERM_KEYS } from 'view/routes/axeBoard/components/axeModelUtils'

export const wasUploadedFromSpreadsheet = (order: Order): boolean => order.source === OrderAttributesCreateSource.SPREADSHEET_UPLOAD

export function isActive(status: NegotiationStatus): boolean {
  const activeStatuses = [
    NegotiationStatus.REQUESTED_WITHOUT_PRICE,
    NegotiationStatus.REQUESTED_WITH_PRICE,
    NegotiationStatus.QUOTED,
    NegotiationStatus.QUOTED_SUBJECT,
    NegotiationStatus.WITHDRAWN,
    NegotiationStatus.ITEMS_IN_DIFFERENT_STATES
  ]
  return activeStatuses.includes(status)
}

export const IsAxedDefault: boolean = false
export const TradeConditionFlagDefault: TradeConditionFlag = TradeConditionFlag.NONE

export function newEmptyNegotiationInstruction(userPreference: UserPreference): NegotiationInstruction {
  const settlementVenue = SettlementVenue.Bilateral

  return {
    rfqDurationMins: userPreference.rfqDurationMinsDefault,
    csd: null,
    externalNegotiationId: null,
    externalSummaryNegotiationId: null,
    settlementVenue,
    clearingType: tradingEntityClearingTypeFromSettlementVenue(settlementVenue),
    clearingHouseId: null,
    sponsorId: null,
    counterpartyInstructions: [],
    hideCompetition: !userPreference.showCompetitionDefault,
    fixExpireTime: null
  }
}

export function spreadsheetSettlementVenueMapper(spreadsheetSettlementVenue: SpreadsheetSettlementVenue): SettlementVenue {
  return {
    [SpreadsheetSettlementVenue.Bilateral]: SettlementVenue.Bilateral,
    [SpreadsheetSettlementVenue.Direct]: SettlementVenue.Direct,
    [SpreadsheetSettlementVenue.Sponsored]: SettlementVenue.Sponsored
  }[spreadsheetSettlementVenue]
}

export function tradingEntityClearingTypeFromSettlementVenue(settlementVenue: Nullable<SettlementVenue>) {
  switch (settlementVenue) {
    case SettlementVenue.Bilateral:
    case SettlementVenue.Triparty:
      return null

    case SettlementVenue.Direct:
      return TradingEntityClearingType.DIRECT

    case SettlementVenue.Sponsored:
      return TradingEntityClearingType.SPONSORED

    default:
      return null
  }
}

export function newEmptyOrderAttributes(
  partialUserEnterAttributes: Partial<OrderUserEnteredAttributes>,
  collectionId: string | null = null,
  collectionName: string | null = null,
  collectionType: CollectionType | null = null,
  source: OrderAttributesCreateSource = OrderAttributesCreateSource.ADD_ASSETS
): OrderAttributes {
  return {
    orderUserEnteredAttributes: {
      parentOrderId: null,
      externalOrderId: null,
      positionId: null,
      collateralId: null,
      csd: null,
      currency: null,
      basketId: null,
      fundId: null,
      fundShortName: null,
      referenceRate: null,
      direction: null,
      notional: null,
      rate: null,
      bondPrice: null,
      startDate: null,
      startDateTerm: null,
      endDate: null,
      endDateTerm: null,
      endDateTerms: null,
      endDates: null,
      term: null,
      book: null,
      strategy: null,
      bundle: null,
      primeBrokerId: null,
      repoType: null,
      startConsideration: null,
      investmentDecisionMaker: null,
      executionDecisionMaker: null,
      callableType: null,
      callablePeriodDays: null,
      noticePeriodDays: null,
      backToStagingTradingType: null,
      blockTrade: null,
      rfqType: null,
      notes: null,
      uploadRowIndex: null,
      allowPartial: false,
      targetFillNotional: null,
      targetFillStartConsideration: null,
      preAgreed: false,
      masterLegalAgreementType: null,
      settlementVenue: null,
      clearingHouseId: null,
      sponsorId: null,
      startConsiderationOverride: false,
      fee: null,
      secondaryBasketId: null,
      secondaryCollateralId: null,
      stockBorrowingLendingType: null,
      evergreenNoticePeriod: null,
      dateSource: null,
      rightOfExit: 1,
      rightOfSubstitution: 1,
      haircutType: null,
      haircut: null,
      ...partialUserEnterAttributes
    },
    negotiationInstruction: null,
    orderCounterparties: [],
    collectionId,
    collectionName,
    collectionType,
    source
  }
}

export const getUploadRowIndexPrefix = () => moment().format('YYYYMMDDHHmmss')
export const generateUploadRowIndex = (prefix: string, rowNum: string | number) => `${prefix}${('0000' + rowNum).substr(-4)}`

export const MAX_BULK_STAGING_ACTION_SIZE = 500
export const MAX_BULK_PREPARE_ACTION_SIZE = 1000

export const orderDetailIsSBL = (orderDetail: OrderDetail) => orderDetail.order.repoType === RepoType.STOCK_BORROWING_LENDING

export const getExcludedTermOptionKeys = (orderDetail: OrderDetail) =>
  orderDetail.productSector?.basketType === CollateralBasketType.GC_PLUS ? OPEN_ORDER_TERM_KEYS : []
